import React, { Fragment, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import { AddOutlined } from "@mui/icons-material"
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Skeleton,
  SvgIcon,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import {
  Button,
  InputText,
  Menu,
  MenuItem,
  NotificationUtils,
  RadioButton,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"
import { t } from "i18next"
import moment from "moment"
import { v4 as uuidv4 } from "uuid"

import { DecisionLoadingCard } from "../../components/cards/DecisionLoadingCard"
import { KonanAvatar } from "../../components/core/Avatar"
import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { OptionsWithExplanationDialog } from "../../components/dialogs/OptionsWithExplanationDialog"
import { VersioningDialog } from "../../components/dialogs/VersioningDialog"
import { SelectWithSearch } from "../../components/form/SelectWithSearch/SelectWithSearch"
import { VersionTag } from "../../components/status/VersionTag"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { CreateScorecardSetRequest, UpdateScorecardsetRequest } from "../../types/custom/projects"
import type { VersionChangeRequest } from "../../types/custom/rules"
import type { PaginatedScoreCardListList } from "../../types/generated/api/PaginatedScoreCardListList"
import type { PaginatedScoreCardSetVersionsListList } from "../../types/generated/api/PaginatedScoreCardSetVersionsListList"
import type { ScoreCardList } from "../../types/generated/api/ScoreCardList"
import { ScoreCardSetCreate } from "../../types/generated/api/ScoreCardSetCreate"
import type { ScoreCardSetGroupRetrieve } from "../../types/generated/api/ScoreCardSetGroupRetrieve"
import type { ScoreCardSetList } from "../../types/generated/api/ScoreCardSetList"
import type { ScoreCardSetRetrieve } from "../../types/generated/api/ScoreCardSetRetrieve"
import type { ScoreCardSetVersionsList } from "../../types/generated/api/ScoreCardSetVersionsList"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { extractScorecardErrorsMessages, getValidMaxMinorVersion } from "../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink, getEnvVar } from "../../utils/genericHelpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../Predictions/Interfaces"
import { ComponentTestingDialog } from "../Predictions/components/ComponentTestingDialog"
import { DataBlock } from "../Ruleset/components/RuleCard"
import { ScorecardExample } from "../Scorecards/components/ScorecardExample"
import { ScoreCardBlock } from "./ScoreCardBlock"

import styles from "./ScorecardSet.module.scss"

// Yup validation schema
const validationSchema = Yup.object({
  name: Yup.string().required(t("Scorecardset.validation.scorecardsetNameRequired", { ns: "scorecards" })),
  scorecards: Yup.array().of(
    Yup.object({
      name: Yup.string().required(t("Scorecardset.validation.scorecardNameRequired", { ns: "scorecards" })),
      uuid: Yup.string().required(t("Scorecardset.validation.scorecardNameRequired", { ns: "scorecards" })),
      weight: Yup.number().typeError(t("Scorecardset.validation.numericalValueRequired", { ns: "scorecards" })),
    }),
  ),
})

export function ScorecardLoadingComponent(): React.ReactElement {
  return (
    <Grid item md={12} container style={{ width: "100%" }}>
      <Card className={styles.loaderScorecard} style={{ width: "100%" }}>
        <CardHeader className={styles.loaderHeader} title={<Skeleton width={200} />} />
        <CardContent className={styles.loaderContent}>
          <Skeleton height={30} />
          <Skeleton height={30} />
        </CardContent>
      </Card>
    </Grid>
  )
}

type Props = {
  isCreate?: boolean
  isLoading?: boolean
  goToFirstPage?: () => void
  handleCancel?: () => void
  uuid?: string
  disableDuplicateButton?: boolean
  duplicateScoreSet?: (uuid: string) => void
  isDuplicate?: boolean
  scorecardset?: string
  isAudit?: boolean
  metaData?: ScoreCardSetList
}

/**
 * single scorecardset component
 * @param {boolean} isCreate current mode for scorecardset
 * @param {boolean} isLoading indicator for scorecardset data loading or not
 * @param {ScoreCardSetList} scorecardset retrieved scorecardset from backend
 * @param {ScoreCardList} allScorecards list contains all scorecardset from backend
 * @param {function} handleCancel handler for opting out of scorecardset creation
 * @param {function} goToFirstPage set current page to 0 after creating scorecardset
 * @returns {React.ReactElement}
 */
export function Scorecardset({
  isCreate = false,
  uuid,
  handleCancel,
  isLoading = false,
  goToFirstPage,
  disableDuplicateButton,
  duplicateScoreSet,
  isDuplicate = false,
  isAudit = false,
  metaData,
}: Readonly<Props>): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  // hooks
  const userEmail = useSelector((state: RootState) => state.auth.email)
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const MuiTheme = useMuiTheme()
  const theme = getTheme()

  const { t } = useTranslation("scorecards")

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve scorecardset", permissions.konan, flattenedKonanPermissions)
  const canRemoveScorecard = useIsPermitted("Delete scorecardset", permissions.konan, flattenedKonanPermissions)

  // indicator for updateMode
  const queryClient = useQueryClient()

  const fullScreen = useMediaQuery(MuiTheme.breakpoints.down("md"))

  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isScorecardSetVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastScoreCardRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView &&
        hasNextScoreCardsPage &&
        !isLoadingScoreCards &&
        !isFetchingNextScoreCardsPage &&
        fetchNextScoreCardsPage()
    },
  })

  //states
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)

  const scorecardsetMinWeight = Number(getEnvVar("SCORECARDSET_MIN_WEIGHT"))
  const scorecardsetMaxWeight = Number(getEnvVar("SCORECARDSET_MAX_WEIGHT"))

  const [isCreateMode, setIsCreateMode] = useState<boolean>(isCreate)
  const isUpdateMode = !!uuid && isCreateMode && !isDuplicate

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)

  const [isExplanationDialogOpen, setIsExplanationDialogOpen] = useState<boolean>(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState<boolean>(false)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)

  // Stores the latest minor version for each major version to determine valid restoration points
  // TODO: Move this state into a separate versioning dialog component along with version related logic
  // for better separation of concerns
  const [maxMinorVersionsMap, setMaxMinorVersionsMap] = useState<Record<string, string>>({})

  const [calculationMethod, setCalculationMethod] = useState<ScoreCardSetCreate.calculation_method>(
    ScoreCardSetCreate.calculation_method.NORMALIZED_VALUE_BY_WEIGHT,
  )

  const { isLoading: isScorecardsetLoading, data: scorecardsetData } = useQuery<
    AxiosResponse<ScoreCardSetGroupRetrieve>,
    AxiosError
  >(["scorecardset", uuid], () => KonanAPI.fetchScorecardset(projectId as string, uuid as string), {
    onSuccess: async ({ data }) => {
      const activeVersion = data?.active_version

      await formik.setValues({
        uuid: data?.uuid,
        name: data?.name,
        scorecards:
          activeVersion?.scorecards?.map((scorecard) => ({
            name: scorecard?.name,
            weight: scorecard?.weight,
            uuid: scorecard?.uuid,
          })) ?? [],
      })

      setCalculationMethod(
        data.active_version?.calculation_method ?? ScoreCardSetCreate.calculation_method.NORMALIZED_VALUE_BY_WEIGHT,
      )
    },
    enabled: canShowDetails && (!!uuid || !!projectId) && (!isCreate || isDuplicate),
  })

  const {
    isLoading: isScorecardSetVersionsListLoading,
    isFetchingNextPage,
    data: scorecardSetVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScoreCardSetVersionsListList>, AxiosError, ScoreCardSetVersionsList>(
    ["scorecardsetVersionsList", uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listScorecardSetVersions({
        projectId: projectId as string,
        scorecardsetId: uuid as string,
        page_size: 10,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ScoreCardSetVersionsList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ScoreCardSetVersionsList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!uuid && isVersionDialogOpen,
      refetchOnMount: true,
    },
  )

  // fetch Version
  const { data: scorecardSetVersionData, isLoading: isScorecardSetVersionDataLoading } = useQuery<
    AxiosResponse<ScoreCardSetRetrieve>,
    AxiosError
  >(
    ["selectedScorecardsetVersion", uuid, selectedVersion],
    () =>
      KonanAPI.retrieveScorecardSetVersion({
        projectId: projectId as string,
        scorecardsetId: uuid as string,
        version: selectedVersion as string,
      }),
    {
      onSuccess: async () => {
        if (
          selectedVersion &&
          !maxMinorVersionsMap[selectedVersion?.split(".")?.[0] as string] &&
          scorecardSetVersionsList?.pages
        ) {
          const result = getValidMaxMinorVersion(scorecardSetVersionsList?.pages, selectedVersion)
          setMaxMinorVersionsMap((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
      enabled: !!projectId && !!uuid && isVersionDialogOpen && !!selectedVersion,
    },
  )

  // fetch project labels
  const {
    data: scorecards,
    isLoading: isLoadingScoreCards,
    isFetchingNextPage: isFetchingNextScoreCardsPage,
    fetchNextPage: fetchNextScoreCardsPage,
    hasNextPage: hasNextScoreCardsPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScoreCardListList>, AxiosError, ScoreCardList>(
    ["scorecards", projectId, searchValue],
    ({ pageParam = 1 }) => KonanAPI.fetchScorecards(projectId as string, pageParam, 20, searchValue),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ScoreCardList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ScoreCardList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId,
      refetchOnMount: true,
    },
  )

  const createScorecardSetsMutation = useMutation<
    AxiosResponse,
    AxiosError<CreateScorecardSetRequest>,
    CreateScorecardSetRequest
  >(KonanAPI.createScorecardSet, {
    mutationKey: "createScorecardset",
    onSuccess: async (response) => {
      await queryClient.invalidateQueries(["scorecardsets", projectId])
      await queryClient.invalidateQueries(["scorecardset", response?.data?.uuid])
      // closing the scorecardset view upon creation
      handleCancel?.()
      NotificationUtils.toast(t("notifications.scorecardsetCreated"), {
        severity: "success",
      })
      setIsCreateMode(false)
      // Redirect to first page after new scorecardset gets created!
      goToFirstPage?.()
    },
    onError: (response: AxiosError<CreateScorecardSetRequest>) => {
      const errorMessage = extractScorecardErrorsMessages(response?.response?.data, "create")
      NotificationUtils.toast(errorMessage, {
        severity: "error",
      })
    },
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateScorecardsetMutation = useMutation<AxiosResponse, AxiosError<any>, UpdateScorecardsetRequest>(
    KonanAPI.updateScorecardset,
    {
      mutationKey: "updateScorecardset",
      onSuccess: async (response) => {
        await queryClient.invalidateQueries(["scorecardsets", projectId])
        await queryClient.invalidateQueries(["scorecardset", response?.data?.uuid])

        NotificationUtils.toast(t("notifications.scorecardsetUpdated"), {
          severity: "success",
        })
        setIsCreateMode(false)
      },
      onError: (response: AxiosError<UpdateScorecardsetRequest>) => {
        const errorMessage = extractScorecardErrorsMessages(response?.response?.data, "update")
        NotificationUtils.toast(errorMessage, {
          severity: "error",
        })
      },
    },
  )

  // change scorecardset version
  const scorecardsetChangeMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
  )

  // scorecardset deletion Mutation
  const deleteScoreCardSetMutation = useMutation<
    AxiosResponse,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    { project_uuid: string; resource_uuid: string }
  >((props) => KonanAPI.deleteWorkflowResource({ ...props, resource_type: "scorecardsets" }), {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.scorecardsetDeleted"), { severity: "success" })

      await queryClient.invalidateQueries(["scorecardsets", projectId])
      await queryClient.invalidateQueries(["scorecardset", uuid])

      setIsDeletionDialogOpen(false)
    },
    onError: ({ response }) => {
      NotificationUtils.toast(response?.data?.details ?? t("notifications.scorecardsetDeleteError"), {
        severity: "error",
      })
    },
  })

  // form control
  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: {
      uuid: "",
      name: "",
      scorecards: [
        {
          name: "",
          weight: undefined as string | undefined,
          uuid: undefined as string | undefined,
        },
      ],
    },
    onSubmit: async ({ scorecards, name }) => {
      const trimmedName = name?.trim()

      // TODO:: refactor all these checks
      // Check if every Scorecard has a weight
      const allScorecardsHaveWeight = scorecards.every((scorecard) => !!scorecard.weight && scorecard.weight !== "")

      // check if some scorecards have weights
      const someScorecardsHaveWeight = scorecards.some((scorecard) => !!scorecard.weight && scorecard.weight !== "")

      // check if some scorecards have invalid weights
      const someScorecardsHaveInvalidWeight = scorecards.some(
        (scorecard) => !!scorecard.weight && Number(scorecard?.weight) > scorecardsetMaxWeight,
      )

      // Calculate the sum of all weights
      const totalWeight = scorecards.reduce((sum, scorecard) => sum + (parseFloat(scorecard?.weight ?? "0") || 0), 0)

      if (someScorecardsHaveWeight && !allScorecardsHaveWeight) {
        NotificationUtils.toast(t("notifications.socreCardsetsWeightError"), {
          severity: "error",
        })
        return
      }

      if (someScorecardsHaveInvalidWeight) {
        NotificationUtils.toast(t("notifications.scoreCardsetsDigitsError"), {
          severity: "error",
        })
        return
      }

      // Check if the sum of all weights is less than 1
      if (
        (allScorecardsHaveWeight && Math.round(totalWeight) < scorecardsetMinWeight) ||
        Math.round(totalWeight) > scorecardsetMaxWeight
      ) {
        // Display a message
        NotificationUtils.toast(
          t("notifications.scorecardsetsWeightsSumError", {
            min: scorecardsetMinWeight,
            max: scorecardsetMaxWeight,
          }),
          {
            severity: "error",
          },
        )
        return
      }

      // Create a new array of objects representing the Scorecards with their UUIDs and weights
      const scorecardsWithUuidsAndWeights = scorecards.map((scorecard) => {
        const uuid = scorecard.uuid ?? ""

        const scorecardSetObject: { uuid: string; weight?: string } = { uuid }

        // Return an object representing the current Scorecard with its UUID and weight
        if (scorecard.weight) {
          scorecardSetObject["weight"] = scorecard.weight
          return scorecardSetObject
        }
        return scorecardSetObject
      })

      if (isUpdateMode) {
        await updateScorecardsetMutation.mutateAsync({
          projectId: projectId as string,
          scorecardsetId: uuid,
          name: trimmedName === scorecardsetData?.data?.name ? undefined : trimmedName,
          calculation_method: calculationMethod,
          scorecards: [...scorecardsWithUuidsAndWeights],
        })
      } else {
        await createScorecardSetsMutation.mutateAsync({
          projectId: projectId as string,
          calculation_method: calculationMethod,
          name: trimmedName,
          scorecards: [...scorecardsWithUuidsAndWeights],
        })
      }
    },
  })

  const usedScorecardsIds = useMemo(() => {
    return new Set(formik.values.scorecards.map((item) => item.uuid))
  }, [formik.values.scorecards])

  const optionsList = (scorecards?.pages ?? [])?.map((scorecard) => {
    const isUsed = usedScorecardsIds.has(scorecard?.uuid)

    return {
      text: scorecard.name,
      value: scorecard.uuid,
      item: (
        <Grid item justifyContent="space-between" container px={0.5}>
          <Typography variant="p" disabled={isUsed}>
            {scorecard.name}
          </Typography>

          {isUsed && <CheckOutlinedIcon fontSize="small" style={{ color: "var(--green-text-2)" }} />}
        </Grid>
      ),
      disabled: isUsed,
    }
  })

  const handleAddScorecard = (): void => {
    formik.setFieldError("name", undefined)

    formik.setFieldTouched("name", false)

    formik.setFieldValue("scorecards", [
      ...formik.values.scorecards,
      {
        name: "",
        weight: undefined,
        uuid: undefined,
      },
    ])
  }

  const handleRemoveScorecard = (cardIdx: number): void => {
    // Update the scorecards array by removing the object representing the Scorecard at the given index
    formik.setFieldValue(
      "scorecards",
      formik.values.scorecards.filter((_, index) => index !== cardIdx),
    )
  }

  const handleCancelButton = (): void => {
    if (isUpdateMode) {
      setIsCreateMode(false)
      const activeVersion = scorecardsetData?.data?.active_version

      formik.setValues({
        uuid: scorecardsetData?.data?.uuid as string,
        name: scorecardsetData?.data?.name as string,
        scorecards:
          activeVersion?.scorecards?.map((scorecard) => ({
            name: scorecard?.name as string,
            weight: scorecard?.weight as string,
            uuid: scorecard?.uuid as string,
          })) ?? [],
      })

      if (scorecardsetData?.data?.active_version?.calculation_method) {
        setCalculationMethod(
          scorecardsetData?.data?.active_version?.calculation_method as ScoreCardSetCreate.calculation_method,
        )
      }
    } else {
      handleCancel?.()
    }
  }

  const changeActiveScorecardsetVersion = async (version: string): Promise<void> => {
    try {
      await scorecardsetChangeMutation.mutateAsync({
        projectId: projectId as string,
        resourceId: uuid as string,
        version: version.toString(),
        resource: "scorecardsets",
      })

      NotificationUtils.toast(`Version (${version}) restored.`, {
        severity: "success",
      })

      // Invalidate react-query queries
      await queryClient.invalidateQueries(["scorecardsets", projectId])
      await queryClient.invalidateQueries(["scorecardset", uuid])
      await queryClient.invalidateQueries(["scorecardsetVersionsList", uuid])
    } catch (error) {
      NotificationUtils.toast(
        t("notifications.scorecardsetsRestoreVersionError", {
          version: version,
        }),
        {
          severity: "error",
        },
      )
    }
  }

  const GetScorecardsetContainer = (): React.ReactElement => {
    const version = scorecardSetVersionData?.data

    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? scorecardsetData?.data?.active_version?.version)?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      maxMinorVersionsMap[majorSelectedVersion as string] ===
        (selectedVersion ?? scorecardsetData?.data?.active_version?.version) &&
      maxMinorVersionsMap[majorSelectedVersion as string] !== scorecardsetData?.data?.active_version?.version

    if (isScorecardSetVersionsListLoading || isScorecardSetVersionDataLoading) {
      return (
        <Grid container xs={12} gap={1} direction={"column"}>
          <Grid item className={"versioning-dialog-header"}>
            <Skeleton height={30} width={300} />
          </Grid>

          <Grid item px={2}>
            <DecisionLoadingCard />
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        {/* Header */}
        <Grid
          item
          xs={12}
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item gap={1} xs={9} display="flex">
            <Typography variant="h2-bold" style={{ textTransform: "inherit", marginRight: "5px" }}>
              {scorecardsetData?.data?.name}
            </Typography>

            <VersionTag
              version={isNaN(Number(version?.version)) ? undefined : (Number(version?.version) ?? selectedVersion)}
              color={
                version
                  ? scorecardsetData?.data?.active_version?.version === selectedVersion
                    ? "green"
                    : "blue"
                  : "green"
              }
            />
          </Grid>

          {shouldShowRestoreButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => changeActiveScorecardsetVersion(selectedVersion as string)}
              disabled={scorecardsetChangeMutation.isLoading}
              isLoading={scorecardsetChangeMutation.isLoading}
            >
              {t("restoreBtn")}
            </Button>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} p={1.5} pt={0}>
          {version?.scorecards &&
            version?.scorecards?.length &&
            version?.scorecards
              ?.toSorted((a, b) => Number(a?.created_at) - Number(b?.created_at))
              ?.map((scorecard) => (
                <Grid
                  container
                  item
                  xs={12}
                  spacing={1}
                  display={"flex"}
                  justifyContent="flex-start"
                  alignItems="center"
                  key={scorecard.uuid}
                  pt={1.5}
                >
                  <Grid item xs={8} md={9.5}>
                    <ScoreCardBlock scorecard={{ name: scorecard.name, uuid: scorecard.uuid }} isEdit={false} />
                  </Grid>

                  <Grid item xs={4} md={2.5} alignSelf={"center"}>
                    <DataBlock value={`${parseInt(scorecard.weight)}%`} />
                  </Grid>
                </Grid>
              ))}
        </Grid>
      </Grid>
    )
  }

  const shouldDisableEditButton = Boolean(
    createScorecardSetsMutation.isLoading ||
      isScorecardsetLoading ||
      formik.isSubmitting ||
      updateScorecardsetMutation.isLoading,
  )

  const shouldDisableDuplicateButton = Boolean(
    createScorecardSetsMutation.isLoading ||
      isScorecardsetLoading ||
      formik.isSubmitting ||
      updateScorecardsetMutation.isLoading ||
      disableDuplicateButton,
  )

  const selectedScorecardSet: SelectedComponent = useMemo(
    () => ({
      id: metaData?.uuid ?? "",
      type: COMPONENT_TYPES.SCORECARDSET,
      data: metaData as ScoreCardSetList,
    }),
    [metaData],
  )

  return (
    <Fragment>
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedScorecardSet}
        />
      )}
      {/* Versioning Dialog*/}
      {isVersionDialogOpen && (
        <VersioningDialog
          activeVersion={scorecardsetData?.data?.active_version as ScoreCardSetRetrieve}
          setSelectedVersion={setSelectedVersion}
          selectedVersion={selectedVersion}
          isOpen={isVersionDialogOpen}
          versions={scorecardSetVersionsList?.pages ?? []}
          onClose={() => setIsVersionDialogOpen(false)}
          infiniteScrollProps={{
            hasNextPage,
            isFetchingNextPage,
            lastVersionRef,
          }}
          height={fullScreen ? "100%" : "400px"}
          maxWidth="md"
        >
          <GetScorecardsetContainer />
        </VersioningDialog>
      )}

      {isExplanationDialogOpen && (
        <OptionsWithExplanationDialog
          setSelectedOption={setCalculationMethod}
          selectedOption={calculationMethod}
          dialogTitle={t("scorecardsetExplinationDialog.title")}
          options={[
            {
              value: "NORMALIZED_VALUE_BY_WEIGHT",
              label: t("scorecardsetExplinationDialog.NORMALIZED_VALUE_BY_WEIGHT.label"),
              description: t("scorecardsetExplinationDialog.NORMALIZED_VALUE_BY_WEIGHT.description"),
              component: <ScorecardExample isEquationNormalized />,
            },
            {
              value: "VALUE_BY_WEIGHT",
              label: t("scorecardsetExplinationDialog.VALUE_BY_WEIGHT.label"),
              description: t("scorecardsetExplinationDialog.VALUE_BY_WEIGHT.description"),
              component: <ScorecardExample />,
            },
          ]}
          open
          onClose={() => setIsExplanationDialogOpen(false)}
          shouldEnableSelectingMethods={Boolean(isCreateMode || isUpdateMode)}
        />
      )}

      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={isDeletionDialogOpen}
          name={scorecardsetData?.data?.name ?? ""}
          onClose={() => setIsDeletionDialogOpen(false)}
          onAccept={() => {
            deleteScoreCardSetMutation.mutateAsync({
              project_uuid: projectId as string,
              resource_uuid: uuid as string,
            })
          }}
          mode={"scorecardset-deletion"}
          isLoading={deleteScoreCardSetMutation.isLoading}
        />
      )}

      {/* Scorecardset Loader */}
      {isLoading ? (
        <DecisionLoadingCard />
      ) : (
        <Card className="card-box-shadow">
          <CardHeader
            className={styles.scorecardSetHeader}
            title={
              <Grid container direction="column" item xs={12} spacing={1}>
                <Grid item container xs={12} justifyContent="space-between">
                  <Grid item container xs={5}>
                    {uuid && !isCreateMode ? (
                      <Grid item mr={1} xs={"auto"} sm={"auto"}>
                        <Typography noWrap variant="h3-bold" className={styles.cardTitle}>
                          {metaData?.name}
                        </Typography>
                      </Grid>
                    ) : (
                      <Grid item xs={6}>
                        <InputText
                          hideDescription
                          id="name"
                          placeholder={t("name")}
                          value={formik.values.name}
                          handleChange={formik.handleChange}
                          error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
                          handleBlur={formik.handleBlur}
                          disabled={
                            formik.isSubmitting || Boolean(updateScorecardsetMutation.isLoading && isUpdateMode)
                          }
                          fullWidth
                        />
                      </Grid>
                    )}

                    {!isCreateMode &&
                      !isUpdateMode &&
                      (metaData?.version || selectedVersion || scorecardsetData?.data.active_version) && (
                        <Grid item mt={isCreateMode || isUpdateMode ? 0 : -1}>
                          <VersionTag
                            version={
                              selectedVersion ?? metaData?.version ?? scorecardsetData?.data.active_version?.version
                            }
                            color={
                              selectedVersion ? (selectedVersion === metaData?.version ? "green" : "blue") : "green"
                            }
                          />
                        </Grid>
                      )}
                  </Grid>

                  {!isCreateMode && !isUpdateMode && !isAudit && (
                    <Grid container item xs justifyContent={"flex-end"}>
                      <IconButton
                        aria-label="settings"
                        size="small"
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
                      >
                        <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
                      </IconButton>

                      <Menu
                        key="basic-menu"
                        anchorEl={anchorEl}
                        open={isMenuOpen}
                        onClose={() => setAnchorEl(null)}
                        menuMaxContent
                      >
                        <MenuItem
                          onClick={() => {
                            setIsTestDialogOpen(true)
                            setAnchorEl(null)
                          }}
                        >
                          <Typography className={styles.optionsMenuItem} variant="a" color="base" variantColor={2}>
                            {t("testScorecardsetBtn")}
                          </Typography>
                        </MenuItem>
                        {canShowDetails && canRemoveScorecard && (
                          <MenuItem
                            onClick={() => {
                              setIsDeletionDialogOpen(true)
                              setAnchorEl(null)
                            }}
                          >
                            <Typography variant="a" color="negative" variantColor={2}>
                              {t("removeScorecardsetBtn")}
                            </Typography>
                          </MenuItem>
                        )}
                      </Menu>
                    </Grid>
                  )}
                </Grid>

                {/* Avatar section */}
                <Grid
                  item
                  sx={{ flexDirection: { xs: "column", md: "row" } }}
                  container
                  xs={12}
                  justifyContent="space-between"
                >
                  <Grid
                    sx={{ flexDirection: { xs: "column", md: "row" } }}
                    item
                    container
                    xs={12}
                    justifyContent="space-between"
                  >
                    <Grid container item spacing={1} xs={6} alignItems="flex-start" className={styles.avatarContainer}>
                      <Grid item sx={{ marginTop: "2px" }}>
                        {/* SvgIcon is used to fix square avatars on safari */}
                        <SvgIcon className={styles.modelAvatar}>
                          <KonanAvatar size={24} name={metaData?.created_by ?? userEmail ?? "user"} />
                        </SvgIcon>
                      </Grid>
                      <Grid container item xs={10}>
                        <Grid item>
                          <Typography variant="label-bold" noWrap style={{ width: "fit-content", marginTop: "0px" }}>
                            {metaData?.created_by ?? userEmail ?? "user"}
                          </Typography>

                          {metaData?.created_at && (
                            <Typography variant="label-regular" noWrap style={{ width: "fit-content" }}>
                              <Tooltip
                                title={moment(metaData?.created_at).format("DD/MM/YYYY, h:mm A")}
                                placement="right"
                              >
                                <Typography
                                  variant="label-regular"
                                  className={styles.date}
                                  style={{ width: "fit-content" }}
                                >
                                  {moment(metaData?.created_at).fromNow()}
                                </Typography>
                              </Tooltip>
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Action Buttons*/}
                    {canShowDetails && (
                      <Grid item container xs={12} sm={6} sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        {isCreateMode ? (
                          <Grid item display="flex" gap={1}>
                            <Button
                              size="small"
                              disabled={Boolean(isScorecardsetLoading || updateScorecardsetMutation.isLoading)}
                              onClick={handleCancelButton}
                              variant={"secondary"}
                            >
                              {t("cancelBtn")}
                            </Button>

                            <Button
                              variant="primary"
                              disabled={
                                isScorecardsetLoading ||
                                updateScorecardsetMutation.isLoading ||
                                formik.values.scorecards.length <= 0
                              }
                              size="small"
                              onClick={formik.submitForm}
                            >
                              {formik.isSubmitting ||
                              createScorecardSetsMutation.isLoading ||
                              updateScorecardsetMutation.isLoading ? (
                                <Grid
                                  container
                                  justifyContent="center"
                                  alignItems="center"
                                  display="flex"
                                  className={styles.loadingContainer}
                                >
                                  <Grid item margin={"auto"}>
                                    <CircularProgress style={{ margin: "auto", verticalAlign: "top" }} size={14} />
                                  </Grid>
                                </Grid>
                              ) : (
                                t("saveBtn")
                              )}
                            </Button>
                          </Grid>
                        ) : (
                          <Fragment>
                            {!isAudit && (
                              <Fragment>
                                <Box mr={1} alignSelf="flex-start" mt={-0.3} mb={0.5}>
                                  <Button
                                    size="small"
                                    variant="secondary"
                                    disabled={shouldDisableDuplicateButton}
                                    onClick={() => duplicateScoreSet?.(scorecardsetData?.data.uuid as string)}
                                  >
                                    {t("duplicateBtn")}
                                  </Button>
                                </Box>

                                <Box mr={1} alignSelf="flex-start" mt={-0.3} mb={0.5}>
                                  <Button
                                    size="small"
                                    variant="secondary"
                                    disabled={Boolean(
                                      createScorecardSetsMutation.isLoading ||
                                        isScorecardsetLoading ||
                                        formik.isSubmitting ||
                                        updateScorecardsetMutation.isLoading,
                                    )}
                                    onClick={() => {
                                      setSelectedVersion(scorecardsetData?.data?.active_version?.version)
                                      setIsVersionDialogOpen(true)
                                    }}
                                  >
                                    {t("historyBtn")}
                                  </Button>
                                </Box>
                              </Fragment>
                            )}
                            <Box alignSelf="flex-start" mt={-0.3} mb={0.5}>
                              <Button
                                variant="secondary"
                                disabled={shouldDisableEditButton}
                                size="small"
                                onClick={() => setIsCreateMode(true)}
                              >
                                {t("editBtn")}
                              </Button>
                            </Box>
                          </Fragment>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {canShowDetails && (
                  <Grid item container xs={12} display="flex" flexDirection={{ xs: "column", lg: "row" }}>
                    <Grid item xs>
                      <Typography variant="p" display="block" style={{ textTransform: "inherit" }}>
                        {t("scoreCardSetDesc")}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            }
          />

          {/* Body */}
          <CardContent className={styles.cardContent}>
            {canShowDetails ? (
              <Fragment>
                <Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={2} mb={1}>
                  <Grid item container display="flex" flexDirection="column">
                    <Typography variant="label-bold" tooltip={t("scorecardCalculationToolTip")}>
                      {t("scorecardCalculation")}
                    </Typography>
                    <Grid item container display="flex" justifyContent="flex-start">
                      {isCreateMode || isUpdateMode ? (
                        <Grid container item width={{ xs: "fit-content" }} spacing={1} flexWrap="nowrap">
                          <Grid item mr={0.5}>
                            <RadioButton
                              value="Value"
                              onChange={() =>
                                setCalculationMethod(ScoreCardSetCreate.calculation_method.NORMALIZED_VALUE_BY_WEIGHT)
                              }
                              checked={
                                calculationMethod === ScoreCardSetCreate.calculation_method.NORMALIZED_VALUE_BY_WEIGHT
                              }
                              label={t("scorecardsetExplinationDialog.NORMALIZED_VALUE_BY_WEIGHT.label")}
                              id={`normalized-value-by-weight-${uuidv4()}`}
                              disabled={formik?.isSubmitting}
                            />
                          </Grid>

                          <Grid item mr={0.5}>
                            <RadioButton
                              value="Feature"
                              onChange={() =>
                                setCalculationMethod(ScoreCardSetCreate.calculation_method.VALUE_BY_WEIGHT)
                              }
                              checked={calculationMethod === ScoreCardSetCreate.calculation_method.VALUE_BY_WEIGHT}
                              label={t("scorecardsetExplinationDialog.VALUE_BY_WEIGHT.label")}
                              id={`Value-by-weight-${uuidv4()}`}
                              disabled={formik?.isSubmitting}
                            />
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid item width="min-content" mt={0.5} mr={1.5}>
                          <DataBlock
                            textVariant="p"
                            value={
                              calculationMethod === ScoreCardSetCreate.calculation_method.VALUE_BY_WEIGHT
                                ? t("scorecardsetExplinationDialog.VALUE_BY_WEIGHT.label")
                                : t("scorecardsetExplinationDialog.NORMALIZED_VALUE_BY_WEIGHT.label")
                            }
                          />
                        </Grid>
                      )}
                      {/* TODO:: replace with SUI anchor component in the revamp
             leaving the styles inline to remember to remove them when revamping */}
                      <Grid
                        display="flex"
                        alignSelf={isCreateMode || isUpdateMode ? "flex-end" : "center"}
                        item
                        ml={1}
                        mt={isCreateMode || isUpdateMode ? "0px" : "4px"}
                        onClick={() => setIsExplanationDialogOpen(true)}
                      >
                        <Link className={"explainAnchor"} underline="none">
                          {t("explainBtn")}
                        </Link>
                      </Grid>
                    </Grid>
                  </Grid>

                  {formik?.values?.scorecards.map((scorecard, index: number) => (
                    <Grid
                      container
                      item
                      xs={12}
                      spacing={1}
                      display={"flex"}
                      justifyContent="flex-start"
                      alignItems="center"
                      key={scorecard.uuid}
                    >
                      <Grid item xs={8} md={9.5}>
                        <ScoreCardBlock
                          scorecard={{ name: scorecard.name, uuid: scorecard.uuid }}
                          isEdit={isCreateMode || isUpdateMode}
                          isLoading={isScorecardsetLoading}
                          handleRemove={() => handleRemoveScorecard(index)}
                          usedScorecardsIds={usedScorecardsIds}
                          onValueChange={(scorecard) => {
                            const formikScoreCards = [...formik.values.scorecards]

                            if (!formikScoreCards[index]) return

                            formikScoreCards[index] = { ...formikScoreCards[index], ...scorecard }

                            formik.setFieldValue("scorecards", formikScoreCards)
                          }}
                        />
                      </Grid>

                      <Grid item xs={4} md={2.5} alignSelf={"center"}>
                        {isCreateMode || isUpdateMode ? (
                          <InputText
                            id={`scorecards[${index}].weight`}
                            placeholder={t("inputWeightPlaceholder")}
                            value={
                              formik.values.scorecards[index]?.weight
                                ? `${parseFloat(formik.values.scorecards[index].weight)}`
                                : undefined
                            }
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            hideDescription
                            disabled={formik.isSubmitting}
                            type="number"
                            fullWidth
                          />
                        ) : (
                          <DataBlock
                            value={`${parseFloat(scorecard.weight as string)}`}
                            isLoading={isScorecardsetLoading}
                          />
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>

                {(isCreateMode || isUpdateMode) && (
                  <Grid container justifyContent="flex-start" alignItems="center" mt={2} mb={0.5}>
                    <Grid item justifyContent="flex-start">
                      <SelectWithSearch
                        optionsList={optionsList}
                        isOptionsLoading={isScorecardsetLoading || isFetchingNextScoreCardsPage}
                        searchInputPlaceholder={t("searchPlaceholder")}
                        inputValue={searchValue}
                        onSelectMenuItem={({ value, text }) => {
                          formik.setFieldValue("scorecards", [
                            ...formik.values.scorecards,
                            {
                              name: text,
                              weight: undefined,
                              uuid: value,
                            },
                          ])
                        }}
                        searchInputChangeHandler={(e: InputChangeEvent) => {
                          setSearchValue(e.target.value as string)
                        }}
                        onMenuClose={() => {
                          setSearchValue(undefined)
                        }}
                        lastMenuItemRef={lastScoreCardRef}
                        customMenuAction={(onClick) => {
                          return (
                            <Button
                              variant="secondary"
                              startIcon={<AddOutlined fontSize="small" htmlColor={theme.palette.base.text[1]} />}
                              onClick={() => {
                                handleAddScorecard()
                                onClick()
                              }}
                              fullWidth
                            >
                              {t("newScorecardBtn")}
                            </Button>
                          )
                        }}
                        customMenuTrigger={(inputRef, onClick) => {
                          return (
                            <Button
                              size="regular"
                              variant="ghost"
                              ref={inputRef}
                              onClick={onClick}
                              disabled={Boolean(
                                createScorecardSetsMutation.isLoading ||
                                  updateScorecardsetMutation.isLoading ||
                                  formik.isSubmitting,
                              )}
                              fullWidth
                            >
                              {t("addCardBtn")}
                            </Button>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Fragment>
            ) : (
              <Grid
                padding={2}
                border={1}
                mb={1}
                borderColor={"var(--base-border-1)"}
                bgcolor={"var(--base-background-1)"}
                borderRadius={1}
              >
                <Typography variant="label-regular" color="base">
                  {t("hiddenDuePermission")}
                </Typography>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}
    </Fragment>
  )
}
